import { discreteAvailabilityFunctions } from "../availabilityDiscrete";
import { offDutyStatuses, getSplitPeriodDuration, type IState } from "../helpers";
import { isSplitCompleteBlockedByViolation } from "../splitSb/blockingViolations";
import { AvailabilityCalculator, type TAvailabilityFactorEntry } from "./helpers/availabilityCalculator";
import { type TComputedAvailabilityResult } from "./helpers/shared";

// ─── helpers ──────────────────────────────────────────────────────────────────

const isOffDutyStatusProgressingReset = (state: IState) =>
    state.current?.status !== "WT" || state.config.workday?.consecutiveOffCountIncludesWt !== false;

/**
 * Returns the predicted off-duty reset date, or undefined when no reset is approaching.
 *
 * workdayResetDate is AvailabilityCalculator.reducedRestResetDate ?? workdayResetDate, shared with
 * Workday, Duty and DailyOff so the calculators agree on when the workday resets. Without a break
 * anchor the workday has already reset and not started again, so there is no reset to come.
 */
const getNextOffDutyMinimumResetDate = (state: IState, workdayResetDate: Date | undefined): Date | undefined => {
    if (offDutyStatuses.indexOf(state.current?.status) === -1 || !isOffDutyStatusProgressingReset(state)
        || state.workday.offDutyMinimum === undefined || !state.workday.consecutiveOffStartTime) {
        return undefined;
    }
    if (!workdayResetDate || workdayResetDate <= state.nextDate) {
        return undefined;
    }
    return workdayResetDate;
};

/**
 * One millisecond ahead, when a driving extension that exists only at this instant disappears.
 *
 * shouldApplyExemptionThisIteration applies an AdverseWeather log strictly before the step's end,
 * except on the final iteration, where it is inclusive. A log exactly at nextDate is therefore
 * applied in the final iteration, to whatever workday is current then. If the workday reset inside
 * the last log segment, that is the new, not-started workday, and the extension shows. One
 * millisecond later the same log falls strictly inside the last real step instead, is applied
 * there to the old workday before checkIfWorkdayReset starts the new one, and startNewWorkday
 * does not carry it across a started workday's reset. Any earlier log in that segment would have
 * met the same fate already, so a not-started workday that reset inside the last segment and
 * still carries drivingExtended is exactly this case. A reset at or before the last real log puts
 * the log in the not-started workday on every evaluation, and its restarts carry the extension.
 */
const getAdverseExtensionDropDate = (state: IState): Date | undefined => {
    if (state.workday.hasStarted || state.workday.drivingExtended === undefined || state.logs.length < 2) {
        return undefined;
    }
    const resetDate = state.workdays[state.workdays.length - 2]?.resetInfo?.eligibleForResetDate;
    const lastLogTime = new Date(state.logs[state.logs.length - 2].dateTime).getTime();
    return resetDate && resetDate.getTime() > lastLogTime ? new Date(state.nextDate.getTime() + 1) : undefined;
};

const getNextPassengerSplitResetDate = (state: IState): Date | undefined => {
    // Mirror the core calculator's symmetric completion gate (splitSb/symmetric.ts): a violation
    // only blocks the split (and thus the reset) when violationPreventsSplitCompletion is set. For
    // USA Passenger that is permanently false (MYG-140471), so this correctly predicts the reset
    // despite the violation; Canada (always true) is unchanged.
    if (state.current?.status !== "SB"
        || (state.intermediateSplitConfig.violationPreventsSplitCompletion && isSplitCompleteBlockedByViolation(state))) {
        return undefined;
    }

    const splitConfig = state.config.splitConfig;
    const splitInfo = state.workday.splitSB;
    if (splitConfig?.type !== "symmetric" || !splitInfo || !splitInfo.inSleeper || splitInfo.periods.length !== 2) {
        return undefined;
    }

    const { splitMinimum, splitTotal } = splitConfig;
    const firstPeriod = splitInfo.periods[0];
    const secondPeriod = splitInfo.periods[1];
    const firstDuration = getSplitPeriodDuration(firstPeriod);
    if (firstDuration < splitMinimum) {
        return undefined;
    }

    const secondPeriodMinimum = Math.max(splitMinimum, splitTotal - firstDuration);
    const secondDuration = getSplitPeriodDuration(secondPeriod);
    if (secondDuration >= secondPeriodMinimum || drivingDroppedBySplitCompletion(state) === 0) {
        return undefined;
    }

    return new Date(secondPeriod.fromDate.getTime() + secondPeriodMinimum);
};

/**
 * How much driving the symmetric split's completion takes off the workday. completeSplit starts
 * a new workday at the first of workday.splitLogs and replays them, so the driving it keeps is the
 * driving inside those logs; whatever driveCount holds beyond that was driven before the split
 * logs began and drops out. With none, the completion leaves Driving where it is.
 */
const drivingDroppedBySplitCompletion = (state: IState): number => {
    const splitLogs = state.workday.splitLogs ?? [];
    let drivingInSplitLogs = 0;
    for (let i = 0; i < splitLogs.length; i++) {
        if (splitLogs[i].status === "D") {
            const end = i + 1 < splitLogs.length ? splitLogs[i + 1].dateTime : state.nextDate;
            drivingInSplitLogs += new Date(end).getTime() - new Date(splitLogs[i].dateTime).getTime();
        }
    }
    return Math.max(0, (state.workday.driveCount ?? 0) - drivingInSplitLogs);
};

const getNextPropertySplitResetDate = (state: IState): Date | undefined => {
    if (offDutyStatuses.indexOf(state.current?.status) === -1 || state.current?.status === "WT") {
        return undefined;
    }

    if (state.customCodes.enablePreventSplitCompleteWhenViolationFix && isSplitCompleteBlockedByViolation(state)) {
        return undefined;
    }

    const splitInfo = state.workday.propertySplitSB;
    const splitConfig = state.config.splitConfig;
    if (!splitInfo || !splitConfig || splitConfig.type !== "asymmetric") {
        return undefined;
    }

    // Codebase constraint: the exact property split reset time cannot be computed stably from
    // current state. The availability calculator may use a different SB+OFF pairing to determine
    // reset eligibility than what we would predict here, causing our candidate time to be later
    // than the actual inflection (violating stability invariant 4 — must be ≤ actual event).
    // Conservative fallback: return state.nextDate (coverage gap) to force re-evaluation at each
    // log boundary when the state actually changes.
    return state.nextDate;
};

export type DrivingConditions = {
    avail: number | undefined;
    isConsuming: boolean;
    isOffDuty: boolean;
    hasDrivingInWorkday: boolean;
    isAtBaseMax: boolean;
};

export class DrivingCalculator extends AvailabilityCalculator {
    private conditions(state: IState): DrivingConditions {
        const avail = discreteAvailabilityFunctions.Driving(state, false);
        const isConsuming = state.current?.status === "D";
        const isOffDuty = offDutyStatuses.indexOf(state.current?.status) !== -1;
        const hasDrivingInWorkday = (state.workday.driveCount ?? 0) > 0;
        const isAtBaseMax = !hasDrivingInWorkday && avail === (state.config.workday?.driveMaximum ?? 0);
        return { avail, isConsuming, isOffDuty, hasDrivingInWorkday, isAtBaseMax };
    }

    extractConditions(state: IState): Record<string, unknown> {
        return this.conditions(state);
    }

    rawAvailability(state: IState): number | undefined {
        return this.conditions(state).avail;
    }

    isConsuming(state: IState): boolean {
        return this.conditions(state).isConsuming;
    }

    exhaustion(state: IState): Date | undefined {
        const { avail, isConsuming } = this.conditions(state);
        if (avail === undefined || !isConsuming) return undefined;
        
        // Guard avail > 0: raw value may be ≤ 0 when already in violation —
        // no future zero-crossing exists in that case.
        if (avail > 0) {
            return new Date(state.nextDate.getTime() + avail);
        }
        return undefined;
    }

    availabilityFactors(state: IState): Record<string, TAvailabilityFactorEntry> | undefined {
        const c = this.conditions(state);
        if (c.avail === undefined) return undefined;

        // On-duty non-driving (ON, WT, etc.): availability is frozen and no reset is approaching.
        // The next event will be triggered by a new log entry. Return undefined.
        if (!c.isConsuming && !c.isOffDuty) {
            return undefined;
        }

        if (c.isConsuming) {
            // When driving: availability decreases at rate -1. Exhaustion is handled by the base class.
            return {};
        }

        // When off-duty: availability is frozen. Evaluate off-duty resets and split restarts.
        // If driver hasn't driven today, and availability is already at base maximum,
        // no reset can increase it — so no inflection will occur.
        if (c.isAtBaseMax) {
            return undefined;
        }

        const factors: Record<string, TAvailabilityFactorEntry> = {};

        const offDutyReset = getNextOffDutyMinimumResetDate(state, this.reducedRestResetDate(state) ?? this.workdayResetDate(state));
        if (offDutyReset) {
            factors.offDutyReset = { factor: () => offDutyReset, label: "workday reset" };
        }

        const adverseExtensionDrop = getAdverseExtensionDropDate(state);
        if (adverseExtensionDrop) {
            factors.adverseExtensionDropped = { factor: () => adverseExtensionDrop, label: "adverse weather extension ends" };
        }

        const splitPassengerResetDate = getNextPassengerSplitResetDate(state);
        if (splitPassengerResetDate) {
            factors.passengerSplitReset = { factor: () => splitPassengerResetDate, label: "split sleeper restart" };
        }

        const splitPropertyResetDate = getNextPropertySplitResetDate(state);
        if (splitPropertyResetDate) {
            factors.UNKNOWN_splitPropertyResetCoverageGap = { factor: () => splitPropertyResetDate, label: "split sleeper restart" };
        }

        // If no reset conditions apply, there are no inflections
        if (Object.keys(factors).length === 0) {
            return undefined;
        }

        return factors;
    }
}

export const extractDrivingConditions = (state: IState): DrivingConditions => {
    return new DrivingCalculator().extractConditions(state) as DrivingConditions;
};

export const computeDrivingResult = (state: IState): TComputedAvailabilityResult | undefined => {
    return new DrivingCalculator().compute(state);
};



